import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  MapPinIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
  CurrencyEuroIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_DELIVERY_SETTINGS } from '../graphql/delivery-queries'
import {
  SET_DELIVERY_ENABLED,
  CREATE_DELIVERY_ZONE,
  UPDATE_DELIVERY_ZONE,
  DELETE_DELIVERY_ZONE,
} from '../graphql/delivery-mutations'
import type { DeliverySettings, DeliveryZone, UpdateDeliveryZoneInput } from '../types/delivery'
import { formatEuros } from '../lib/format'
import LoadingSpinner from '../components/LoadingSpinner'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'

// Money lives in the form as the RAW STRING the operator typed ("2,50", "2.5",
// ""), never as cents — a cents round-trip reformats the field mid-keystroke.
// It is parsed once, on save.
interface FormState {
  postcode: string
  fee: string
  minOrder: string
  freeAbove: string
  active: boolean
}

type FormErrors = Partial<Record<'postcode' | 'fee' | 'minOrder' | 'freeAbove', string>>

const EMPTY_FORM: FormState = { postcode: '', fee: '', minOrder: '', freeAbove: '', active: true }

/** Cents → the string the money inputs hold. 0 is the "unset" sentinel, so it shows blank. */
const centsToInput = (cents: number): string => (cents > 0 ? (cents / 100).toFixed(2) : '')

/** Raw input → gross cents. Empty = 0; a Dutch comma is accepted. `null` = unparseable. */
const parseMoney = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (!trimmed) return 0
  // Strict shape, not bare parseFloat: "1.250,00" would silently become
  // € 1,25 and "2e3" € 2000 — reject anything but digits + optional 2 decimals.
  if (!/^\d+([.,]\d{1,2})?$/.test(trimmed)) return null
  return Math.round(parseFloat(trimmed.replace(',', '.')) * 100)
}

/** 0 is a sentinel (no minimum / never free), not an amount — render it as an em dash. */
const formatSentinel = (cents: number): string => (cents > 0 ? formatEuros(cents) : '—')

const DeliverySettingsPage = () => {
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMISSIONS.DELIVERY_MANAGE)

  const { data, loading, error, refetch } = useQuery(GET_DELIVERY_SETTINGS, {
    fetchPolicy: 'cache-and-network',
  })
  const settings: DeliverySettings | undefined = data?.deliverySettings
  const zones: DeliveryZone[] = settings?.zones ?? []

  const [editing, setEditing] = useState<DeliveryZone | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})

  // DeliverySettings carries no id, so Apollo cannot merge the mutation
  // payload into the GET_DELIVERY_SETTINGS cache entry on its own — write it
  // explicitly, making the mutation's single round trip authoritative.
  const [setDeliveryEnabled, { loading: togglingEnabled }] = useMutation(SET_DELIVERY_ENABLED, {
    update: (cache, { data: result }) => {
      if (result?.setDeliveryEnabled) {
        cache.writeQuery({
          query: GET_DELIVERY_SETTINGS,
          data: { deliverySettings: result.setDeliveryEnabled },
        })
      }
    },
  })
  const [createZone, { loading: creatingZone }] = useMutation(CREATE_DELIVERY_ZONE)
  const [updateZone, { loading: updatingZone }] = useMutation(UPDATE_DELIVERY_ZONE)
  const [deleteZone] = useMutation(DELETE_DELIVERY_ZONE)
  const saving = creatingZone || updatingZone

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setErrors({})
    setEditing(null)
    setCreating(true)
  }

  const openEdit = (zone: DeliveryZone) => {
    setForm({
      postcode: zone.postcode,
      fee: (zone.fee_cents / 100).toFixed(2),
      minOrder: centsToInput(zone.min_order_cents),
      freeAbove: centsToInput(zone.free_above_cents),
      active: zone.active,
    })
    setErrors({})
    setEditing(zone)
    setCreating(false)
  }

  const close = () => {
    setCreating(false)
    setEditing(null)
    setErrors({})
  }

  // Mutation failures are already surfaced by the global errorLink in
  // src/lib/apollo.ts, which toasts every GraphQL error message — adding a
  // toast.error here would double it.
  const handleToggleEnabled = async (next: boolean) => {
    if (
      !next &&
      !window.confirm(
        'Turn delivery off? Checkout will reject delivery orders immediately and the public zone list goes empty. Your zones and their prices are kept.'
      )
    ) {
      return
    }
    try {
      await setDeliveryEnabled({ variables: { enabled: next } })
      toast.success(next ? 'Delivery turned on' : 'Delivery turned off')
    } catch {
      // errorLink already toasted; leave the switch showing the server's truth.
    }
  }

  const handleSave = async () => {
    const nextErrors: FormErrors = {}

    const postcode = form.postcode.trim()
    if (!/^\d{4}$/.test(postcode)) {
      nextErrors.postcode = 'Postcode must be exactly 4 digits, e.g. 5654.'
    }

    let feeCents: number | null = null
    if (!form.fee.trim()) {
      nextErrors.fee = 'A fee is required — enter 0.00 for free delivery.'
    } else {
      feeCents = parseMoney(form.fee)
      if (feeCents === null) nextErrors.fee = 'Enter an amount of 0.00 or more.'
    }

    const minCents = parseMoney(form.minOrder)
    if (minCents === null) nextErrors.minOrder = 'Enter an amount of 0.00 or more.'

    const freeCents = parseMoney(form.freeAbove)
    if (freeCents === null) nextErrors.freeAbove = 'Enter an amount of 0.00 or more.'

    if (Object.keys(nextErrors).length > 0 || feeCents === null || minCents === null || freeCents === null) {
      setErrors(nextErrors)
      return
    }
    setErrors({})

    try {
      if (editing) {
        // Partial update — send only what actually changed.
        const input: UpdateDeliveryZoneInput = {}
        if (postcode !== editing.postcode) input.postcode = postcode
        if (feeCents !== editing.fee_cents) input.fee_cents = feeCents
        if (minCents !== editing.min_order_cents) input.min_order_cents = minCents
        if (freeCents !== editing.free_above_cents) input.free_above_cents = freeCents
        if (form.active !== editing.active) input.active = form.active

        if (Object.keys(input).length === 0) {
          close()
          return
        }

        await updateZone({ variables: { id: editing.id, input } })
        toast.success('Zone updated')
      } else {
        await createZone({
          variables: {
            input: {
              postcode,
              fee_cents: feeCents,
              min_order_cents: minCents,
              free_above_cents: freeCents,
              active: form.active,
            },
          },
        })
        toast.success('Zone created')
      }
      close()
      void refetch()
    } catch (err) {
      // Keep the modal open so the entry isn't lost. The duplicate-postcode
      // case is the one worth pinning to its field; everything else was
      // already toasted by errorLink.
      if (err instanceof Error && err.message.includes('already exists')) {
        setErrors({ postcode: err.message })
      }
    }
  }

  const handleTogglePause = async (zone: DeliveryZone) => {
    try {
      await updateZone({ variables: { id: zone.id, input: { active: !zone.active } } })
      toast.success(zone.active ? 'Zone paused' : 'Zone resumed')
      void refetch()
    } catch {
      // errorLink already toasted.
    }
  }

  const handleDelete = async (zone: DeliveryZone) => {
    if (!window.confirm(`Delete zone ${zone.postcode}? The zone and its prices are gone for good.`)) return
    try {
      await deleteZone({ variables: { id: zone.id } })
      toast.success('Zone deleted')
      void refetch()
    } catch {
      // errorLink already toasted.
    }
  }

  const isOpen = creating || !!editing
  const enabled = settings?.enabled ?? false

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Delivery Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Our own delivery area: which postcodes we serve and what they cost.
          </p>
        </div>
        {canManage && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <PlusIcon className="h-5 w-5" />
            Add zone
          </button>
        )}
      </div>

      {/* Content */}
      {loading && !settings ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : error && !settings ? (
        <div className="text-center py-12 text-red-600">{error.message}</div>
      ) : (
        <>
          {/* Global on/off */}
          <div className="card">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  Delivery is {enabled ? 'on' : 'off'}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Turning it off closes the delivery channel instantly: checkout rejects delivery
                  orders and the public zone list goes empty. The zones below and their prices are
                  kept, so switching it back on restores everything.
                </div>
              </div>
              {canManage && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label="Delivery enabled"
                  disabled={togglingEnabled}
                  onClick={() => void handleToggleEnabled(!enabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                    enabled ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                      enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Zones */}
          {zones.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
              <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-sm font-semibold text-gray-900">No delivery zones yet</h3>
              <p className="mt-2 text-sm text-gray-500">
                A zone is a 4-digit postcode we deliver to, with its own fee.
              </p>
              {canManage && (
                <button onClick={openCreate} className="btn-primary mt-6 inline-flex items-center gap-2">
                  <PlusIcon className="h-5 w-5" />
                  Add zone
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="card overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Postcode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Min. order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Free above
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {canManage ? 'Actions' : <span className="sr-only">Actions</span>}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {zones.map((zone) => (
                      <tr key={zone.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {zone.postcode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {formatEuros(zone.fee_cents)}
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                          title={zone.min_order_cents > 0 ? undefined : 'No minimum order'}
                        >
                          {formatSentinel(zone.min_order_cents)}
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                          title={zone.free_above_cents > 0 ? undefined : 'The fee never drops to free'}
                        >
                          {formatSentinel(zone.free_above_cents)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {zone.active ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Paused
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                          {canManage && (
                            <>
                              <button
                                onClick={() => openEdit(zone)}
                                className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                              >
                                <PencilIcon className="h-4 w-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => void handleTogglePause(zone)}
                                className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                              >
                                {zone.active ? (
                                  <>
                                    <PauseIcon className="h-4 w-4" />
                                    Pause
                                  </>
                                ) : (
                                  <>
                                    <PlayIcon className="h-4 w-4" />
                                    Resume
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => void handleDelete(zone)}
                                className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                              >
                                <TrashIcon className="h-4 w-4" />
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400">
                Amounts include VAT. A postcode covers every address starting with those 4 digits.
                “—” means no minimum order / the fee never drops to free.
              </p>
            </div>
          )}
        </>
      )}

      {/* Create / edit modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* While a save is in flight the modal must not be dismissable —
                the duplicate-postcode error pins to the postcode field, and a
                close() racing the response would throw the entry away. */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              onClick={() => {
                if (!saving) close()
              }}
            />
            <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editing ? `Edit zone ${editing.postcode}` : 'New zone'}
                </h3>
                <button onClick={close} disabled={saving} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                {/* Postcode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={form.postcode}
                    onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))}
                    placeholder="5654"
                    className={`input-field ${errors.postcode ? 'border-red-300 bg-red-50' : ''}`}
                  />
                  {errors.postcode ? (
                    <p className="mt-1 text-xs text-red-600">{errors.postcode}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">
                      4 digits, e.g. 5654 — covers every address starting with it.
                    </p>
                  )}
                </div>

                {/* Fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fee <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={form.fee}
                      onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))}
                      placeholder="0.00"
                      className={`input-field pl-10 ${errors.fee ? 'border-red-300 bg-red-50' : ''}`}
                    />
                  </div>
                  {errors.fee ? (
                    <p className="mt-1 text-xs text-red-600">{errors.fee}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">Including VAT.</p>
                  )}
                </div>

                {/* Minimum order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum order</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={form.minOrder}
                      onChange={(e) => setForm((f) => ({ ...f, minOrder: e.target.value }))}
                      placeholder="0.00"
                      className={`input-field pl-10 ${errors.minOrder ? 'border-red-300 bg-red-50' : ''}`}
                    />
                  </div>
                  {errors.minOrder ? (
                    <p className="mt-1 text-xs text-red-600">{errors.minOrder}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">Leave empty for no minimum.</p>
                  )}
                </div>

                {/* Free above */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Free above</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={form.freeAbove}
                      onChange={(e) => setForm((f) => ({ ...f, freeAbove: e.target.value }))}
                      placeholder="0.00"
                      className={`input-field pl-10 ${errors.freeAbove ? 'border-red-300 bg-red-50' : ''}`}
                    />
                  </div>
                  {errors.freeAbove ? (
                    <p className="mt-1 text-xs text-red-600">{errors.freeAbove}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">
                      Order total from which delivery is free. Leave empty to always charge the fee.
                    </p>
                  )}
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  Active (we deliver to this postcode)
                </label>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
                <button onClick={close} disabled={saving} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={() => void handleSave()} disabled={saving} className="btn-primary">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeliverySettingsPage
